// The SQL query builder's LAUNCH point, shared by every SQL input.
//
// The builder itself (a large modal talking to /api/query-builder) lives in
// the PLATFORM frontend — it needs authoring endpoints a downloaded runtime
// does not have. A field only renders <SqlBuilderButton>; the button opens
// whatever builder the host PROVIDED through SqlBuilderLauncherContext, and
// renders NOTHING when no host provided one (the runtimes, where the kit's
// editors also run). One button, one contract, every surface.
//
//   <SqlBuilderButton value={sql} onChange={setSql} mode="where"
//                     contextTables={['customer']} project={project} />
//
// `mode`: 'select' — a whole query; 'where' — a WHERE condition; 'expression'
// — one value expression. A fragment is built against `contextTables` (the
// table(s) the host runs it over). `target` preselects the database
// ('project' | 'deployment' | 'conn:<CONNECTION_ID>').

import { createContext, useContext, type CSSProperties } from 'react'
import { Blocks } from 'lucide-react'

export type SqlBuilderMode = 'select' | 'where' | 'expression'

export interface SqlBuilderRequest {
  sql: string
  mode?: SqlBuilderMode
  project?: string
  target?: string
  contextTables?: string[]
  title?: string
  /** Names this field's saved canvas layout (where the tables sit). The host
   *  derives one from the screen + title when the field gives none. */
  layoutKey?: string
  /** Called with the built SQL when the person presses Apply. `target` is the
   *  database they built against (a host that stores a connection id may
   *  want it). */
  onApply: (sql: string, meta: { target: string }) => void
}

export interface SqlBuilderLauncher {
  open: (request: SqlBuilderRequest) => void
}

export const SqlBuilderLauncherContext = createContext<SqlBuilderLauncher | null>(null)

/** The host's builder, or null when this host has none. */
export function useSqlBuilder(): SqlBuilderLauncher | null {
  return useContext(SqlBuilderLauncherContext)
}

export interface SqlBuilderButtonProps {
  value: string
  onChange: (sql: string) => void
  mode?: SqlBuilderMode
  project?: string
  target?: string
  contextTables?: string[]
  title?: string
  /** See SqlBuilderRequest.layoutKey. */
  layoutKey?: string
  /** Button text; default "Query builder" ("Build condition" / "Build
   *  expression" for fragments). */
  label?: string
  /** Icon-only button (for tight inline inputs). */
  compact?: boolean
  disabled?: boolean
  style?: CSSProperties
  onTargetChange?: (target: string) => void
}

const DEFAULT_LABEL: Record<SqlBuilderMode, string> = {
  select: 'Query builder',
  where: 'Build condition',
  expression: 'Build expression',
}

export function SqlBuilderButton({
  value, onChange, mode = 'select', project, target, contextTables, title, layoutKey,
  label, compact, disabled, style, onTargetChange,
}: SqlBuilderButtonProps) {
  const launcher = useSqlBuilder()
  if (!launcher) return null
  const text = label ?? DEFAULT_LABEL[mode]
  return (
    <button
      type="button"
      data-tw-action="open-query-builder"
      className="tw-sql-builder-btn"
      disabled={disabled}
      title={`${text} — build this SQL visually, check it and tune it`}
      aria-label={text}
      onClick={() => launcher.open({
        sql: value || '', mode, project, target, contextTables, title, layoutKey,
        onApply: (sql, meta) => {
          onChange(sql)
          if (meta?.target && onTargetChange) onTargetChange(meta.target)
        },
      })}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: compact ? '3px 6px' : '3px 9px',
        fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4,
        color: 'var(--color-accent, #0061D0)',
        background: 'var(--color-accent-weak, rgba(0,97,208,.10))',
        border: '1px solid transparent', borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
        whiteSpace: 'nowrap', flexShrink: 0,
        ...style,
      }}
    >
      <Blocks size={14} aria-hidden />
      {!compact && <span>{text}</span>}
    </button>
  )
}

export default SqlBuilderButton
